import { useEffect, useState } from "react";
import "./Toaster.css";

export type ToastKind = "success" | "error" | "info";

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: { label: string; onClick: () => void };
}

type Listener = (toasts: Toast[]) => void;

let nextId = 1;
let toasts: Toast[] = [];
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(toasts);
}

function push(kind: ToastKind, message: string, action?: Toast["action"], persistent = false): number {
  const id = nextId++;
  toasts = [...toasts, { id, kind, message, action }];
  emit();
  if (!persistent) {
    const ttl = kind === "error" ? 8000 : 4000;
    window.setTimeout(() => dismiss(id), ttl);
  }
  return id;
}

export function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Test-only: clears queued toasts and listeners. */
export function _resetToastsForTest() {
  toasts = [];
  listeners.clear();
  emit();
}

export const toast = {
  success: (msg: string) => push("success", msg),
  error: (msg: string, action?: Toast["action"]) => push("error", msg, action),
  info: (msg: string, action?: Toast["action"]) => push("info", msg, action),
  /** A toast with no auto-dismiss timer — remove it explicitly via dismiss(id). */
  persistent: (kind: ToastKind, msg: string, action?: Toast["action"]) => push(kind, msg, action, true),
};

export function Toaster() {
  const [items, setItems] = useState<Toast[]>(toasts);
  useEffect(() => {
    listeners.add(setItems);
    return () => { listeners.delete(setItems); };
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="toaster" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toaster__item toaster__item--${t.kind}`}>
          <span className="toaster__message">{t.message}</span>
          {t.action && (
            <button
              type="button"
              className="toaster__action"
              onClick={() => {
                t.action?.onClick();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            className="toaster__close"
            onClick={() => dismiss(t.id)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
